import { useState, useEffect } from "react";
import { useApi } from "@/lib/api";
import { GitCompare, Check, X, Minus } from "lucide-react";

export default function ComplianceComparison() {
  const api = useApi();
  const [comparison, setComparison] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [comparisonRes, platformsRes] = await Promise.all([
        api.get("/api/analysis/compliance-comparison"),
        api.get("/api/platforms")
      ]);
      setComparison(comparisonRes.data);
      setPlatforms(platformsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderCapabilityCell = (data) => {
    if (!data) {
      return (
        <td className="text-center">
          <span className="text-zinc-300">
            <Minus size={16} />
          </span>
        </td>
      );
    }

    const { in_house, opportunity, complexity } = data;
    
    return (
      <td className="text-center">
        <div className="flex flex-col items-center gap-1">
          {in_house ? (
            <span className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
              <Check size={16} />
            </span>
          ) : (
            <span className="w-8 h-8 bg-violet-100 text-violet-600 rounded-full flex items-center justify-center">
              <X size={16} />
            </span>
          )}
          <span className={`text-xs font-medium ${
            opportunity === 'full' ? 'text-violet-600' :
            opportunity === 'partial' ? 'text-amber-600' :
            'text-green-600'
          }`}>
            {opportunity}
          </span>
        </div>
      </td>
    );
  };

  if (loading) {
    return (
      <div className="content-wrapper flex items-center justify-center min-h-[50vh]">
        <div className="loading-spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="content-wrapper" data-testid="comparison-page">
      <div className="page-header">
        <h1 className="page-title">Compliance Comparison</h1>
        <p className="page-subtitle">
          Side-by-side comparison of platform compliance capabilities and service opportunities
        </p>
      </div>

      {/* Legend */}
      <div className="bg-white border border-zinc-200 rounded-sm p-4 mb-6">
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
              <Check size={12} />
            </span>
            <span className="text-sm text-zinc-600">In-House Capability</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 bg-violet-100 text-violet-600 rounded-full flex items-center justify-center">
              <X size={12} />
            </span>
            <span className="text-sm text-zinc-600">Gap / Service Opportunity</span>
          </div>
          <div className="flex items-center gap-3 ml-4 border-l border-zinc-200 pl-4">
            <span className="text-xs font-medium text-violet-600">full</span>
            <span className="text-xs text-zinc-400">= Full outsource opportunity</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-amber-600">partial</span>
            <span className="text-xs text-zinc-400">= Partial opportunity</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-green-600">none</span>
            <span className="text-xs text-zinc-400">= No opportunity</span>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="comparison-table">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-700 w-1/4">
                  Compliance Category
                </th>
                {platforms.map((platform) => (
                  <th key={platform.id} className="text-center px-6 py-4 text-sm font-semibold text-zinc-700">
                    <div className="flex flex-col items-center gap-1">
                      <span>{platform.name}</span>
                      <span className="text-xs font-normal text-zinc-400">
                        {platform.markets.length} markets
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparison.map((row, idx) => (
                <tr 
                  key={row.category} 
                  className={`border-b border-zinc-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-zinc-50/50'}`}
                >
                  <td className="px-6 py-4 text-sm font-medium text-zinc-900">
                    {row.category}
                  </td>
                  {platforms.map((platform) => 
                    renderCapabilityCell(row[platform.id])
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        {platforms.map((platform) => {
          const capabilities = platform.capabilities;
          const inHouse = capabilities.filter(c => c.in_house).length;
          const gaps = capabilities.filter(c => !c.in_house).length;
          const fullOutsource = capabilities.filter(c => c.outsource_opportunity === 'full').length;
          
          return (
            <div 
              key={platform.id}
              className="bg-white border border-zinc-200 rounded-sm p-6"
            >
              <h3 className="text-lg font-semibold text-zinc-900 mb-4">{platform.name}</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-zinc-600">In-House</span>
                  <span className="text-sm font-semibold text-green-600">{inHouse}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-zinc-600">Gaps</span>
                  <span className="text-sm font-semibold text-violet-600">{gaps}</span>
                </div>
                <div className="flex justify-between items-center border-t border-zinc-100 pt-3">
                  <span className="text-sm text-zinc-600">Full Outsource Opportunities</span>
                  <span className="text-sm font-bold text-violet-600">{fullOutsource}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-zinc-100">
                <div className="text-xs text-zinc-500 mb-1">Service Opportunity Score</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-violet-600 rounded-full transition-all"
                      style={{ width: `${(fullOutsource / capabilities.length) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold text-violet-600">
                    {Math.round((fullOutsource / capabilities.length) * 100)}%
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Key Insights */}
      <div className="bg-violet-50 border border-violet-200 rounded-sm p-6 mt-6">
        <h3 className="text-lg font-semibold text-zinc-900 mb-3">Key Insights</h3>
        <ul className="space-y-2">
          <li className="flex items-start gap-2 text-sm text-zinc-700">
            <span className="w-1.5 h-1.5 bg-violet-600 rounded-full mt-1.5 flex-shrink-0"></span>
            <span><strong>Temu</strong> presents the highest service opportunity with minimal in-house compliance support</span>
          </li>
          <li className="flex items-start gap-2 text-sm text-zinc-700">
            <span className="w-1.5 h-1.5 bg-violet-600 rounded-full mt-1.5 flex-shrink-0"></span>
            <span><strong>Amazon</strong> has robust reporting APIs but still lacks PRO registration and fee optimization support</span>
          </li>
          <li className="flex items-start gap-2 text-sm text-zinc-700">
            <span className="w-1.5 h-1.5 bg-violet-600 rounded-full mt-1.5 flex-shrink-0"></span>
            <span><strong>GPAIS</strong> provides registry infrastructure but requires external support for compliance advisory</span>
          </li>
          <li className="flex items-start gap-2 text-sm text-zinc-700">
            <span className="w-1.5 h-1.5 bg-violet-600 rounded-full mt-1.5 flex-shrink-0"></span>
            <span>Cross-platform opportunity: <strong>Multi-market compliance coordination</strong> is a gap across all platforms</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
